import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlass, PencilSimple } from '@phosphor-icons/react';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const LIMIT_FIELDS = [
  ['max_discovery_jobs', 'admin.limitLabels.discoveryJobs'],
  ['max_companies', 'admin.limitLabels.companies'],
  ['max_contacts', 'admin.limitLabels.contacts'],
  ['max_exports', 'admin.limitLabels.exports'],
  ['max_export_records', 'admin.limitLabels.exportRecords'],
  ['max_contact_reveals', 'admin.limitLabels.contactReveals'],
  ['max_seats', 'admin.limitLabels.seats'],
  ['max_pushed_leads', 'admin.limitLabels.pushedLeads'],
];

const FEATURE_FIELDS = [
  ['has_pro_email_integration', 'admin.featureLabels.proEmailIntegration'],
  ['has_bulk_verification', 'admin.featureLabels.bulkVerification'],
  ['has_personal_contact_discovery', 'admin.featureLabels.personalContactDiscovery'],
  ['has_custom_scoring', 'admin.featureLabels.customScoring'],
  ['has_api_access', 'admin.featureLabels.apiAccess'],
  ['has_priority_support', 'admin.featureLabels.prioritySupport'],
];

const EMPTY_FORM = {
  plan_name: '', force_access: 'inherit', starts_at: '', expires_at: '', reason: '', limits: {}, features: {},
};

export default function SubscriptionAdminPage() {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [clearReason, setClearReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accountResponse, planResponse] = await Promise.all([
        api.get('/super-admin/subscriptions/accounts'), api.get('/saas/plans'),
      ]);
      setAccounts(accountResponse.data.items || []);
      setPlans(planResponse.data || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return accounts;
    return accounts.filter((item) => `${item.account_name} ${item.owner_email}`.toLowerCase().includes(needle));
  }, [accounts, search]);

  const openOverride = (account) => {
    const override = account.entitlements?.override || {};
    setSelected(account);
    setClearReason('');
    setForm({
      plan_name: override.plan_id ? account.entitlements?.effective_plan?.name || '' : '',
      force_access: override.force_access === true ? 'allow' : override.force_access === false ? 'deny' : 'inherit',
      starts_at: toLocalInput(override.starts_at),
      expires_at: toLocalInput(override.expires_at),
      reason: override.reason || '',
      limits: override.limits || {},
      features: override.features || {},
    });
  };

  const grantFullAccess = () => {
    setForm((old) => ({
      ...old,
      plan_name: plans.some((plan) => plan.name === 'pro') ? 'pro' : old.plan_name,
      force_access: 'allow',
      limits: Object.fromEntries(LIMIT_FIELDS.map(([field]) => [field, -1])),
      features: Object.fromEntries(FEATURE_FIELDS.map(([field]) => [field, true])),
    }));
  };

  const saveOverride = async () => {
    if (!selected || form.reason.trim().length < 3) return;
    setSaving(true);
    try {
      const limits = Object.fromEntries(
        Object.entries(form.limits).filter(([, value]) => value !== '' && value != null).map(([key, value]) => [key, Number(value)]),
      );
      await api.put(`/super-admin/subscriptions/accounts/${selected.account_id}/override`, {
        plan_name: form.plan_name || null,
        force_access: form.force_access === 'inherit' ? null : form.force_access === 'allow',
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        reason: form.reason.trim(),
        limits,
        features: Object.fromEntries(
          Object.entries(form.features).map(([key, value]) => [key, Boolean(value)]),
        ),
      });
      toast.success(t('admin.saved'));
      setSelected(null);
      await load();
    } catch (error) {
      toast.error(formatApiError(error) || t('admin.error'));
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async () => {
    if (!selected || clearReason.trim().length < 3) return;
    setSaving(true);
    try {
      await api.post(`/super-admin/subscriptions/accounts/${selected.account_id}/override/clear`, { reason: clearReason.trim() });
      toast.success(t('admin.cleared'));
      setSelected(null);
      await load();
    } catch (error) {
      toast.error(formatApiError(error) || t('admin.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="subscription-admin-page">
      <div>
        <h1 className="font-heading text-2xl font-bold">{t('admin.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('admin.subtitle')}</p>
      </div>
      <div className="relative max-w-lg">
        <MagnifyingGlass size={16} className="absolute left-3 top-2.5 text-muted-foreground" />
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('admin.search')} className="pl-9" />
      </div>
      <div className="overflow-x-auto rounded-lg border border-border bg-background">
        <table className="w-full min-w-[1100px] text-sm">
          <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="p-3">{t('admin.account')}</th>
              <th className="p-3">{t('admin.owner')}</th>
              <th className="p-3">{t('admin.billed')}</th>
              <th className="p-3">{t('admin.effective')}</th>
              <th className="p-3">{t('admin.access')}</th>
              <th className="p-3">Usage</th>
              <th className="p-3">Last login</th>
              <th className="p-3 text-right">{t('admin.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((account) => {
              const entitlements = account.entitlements || {};
              const usage = entitlements.usage || {};
              const limits = entitlements.limits || {};
              const sub = entitlements.subscription || {};
              const trialPaid = sub.status === 'trialing' ? 'Trial' : (sub.status === 'active' ? 'Paid' : (sub.status || '—'));
              return (
                <tr key={account.account_id} className="border-b last:border-0" data-testid={`admin-account-${account.account_id}`}>
                  <td className="p-3 font-medium">{account.account_name}</td>
                  <td className="p-3 text-muted-foreground">{account.owner_email}</td>
                  <td className="p-3">
                    {entitlements.billed_plan?.display_name || '—'}
                    <div className="text-xs text-muted-foreground">{trialPaid} · {sub.status}</div>
                    {sub.current_period_end && (
                      <div className="text-xs text-muted-foreground">Renews {new Date(sub.current_period_end).toLocaleDateString()}</div>
                    )}
                  </td>
                  <td className="p-3">
                    {entitlements.effective_plan?.display_name || '—'}
                    {entitlements.override?.active && <div className="text-xs text-violet-600">{entitlements.override.reason}</div>}
                  </td>
                  <td className="p-3">
                    <span className={`rounded-full px-2 py-1 text-xs ${entitlements.access_allowed ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'}`}>
                      {entitlements.access_allowed ? t('admin.active') : t('admin.readOnly')}
                    </span>
                  </td>
                  <td className="p-3 font-mono text-xs text-muted-foreground">
                    jobs {usage.discovery_jobs || 0}/{limits.max_discovery_jobs ?? '—'}
                    <br />reveals {usage.contacts_revealed || 0}/{limits.max_contact_reveals ?? '—'}
                    <br />exports {usage.exports || 0}/{limits.max_exports ?? '—'}
                    <br />pushes {usage.pushed_leads || 0}/{limits.max_pushed_leads ?? '—'}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {account.owner_last_login_at ? new Date(account.owner_last_login_at).toLocaleString() : '—'}
                  </td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => openOverride(account)}>
                      <PencilSimple size={14} className="mr-1" />{t('admin.edit')}
                    </Button>
                  </td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-muted-foreground">—</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{t('admin.overrideTitle')}</DialogTitle><DialogDescription>{selected?.account_name} · {selected?.owner_email}</DialogDescription></DialogHeader>
          <div className="flex items-center justify-between gap-3 rounded-md border border-violet-200 bg-violet-50 p-3 dark:border-violet-900 dark:bg-violet-950/30">
            <p className="text-sm text-muted-foreground">{t('admin.fullAccessHelp')}</p>
            <Button type="button" variant="outline" className="shrink-0" onClick={grantFullAccess}>{t('admin.fullAccess')}</Button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('admin.plan')}><select value={form.plan_name} onChange={(event) => setForm((old) => ({ ...old, plan_name: event.target.value }))} className="h-9 w-full rounded-md border bg-background px-3 text-sm"><option value="">{t('admin.keepPlan')}</option>{plans.map((plan) => <option key={plan.name} value={plan.name}>{plan.display_name}</option>)}</select></Field>
            <Field label={t('admin.forceAccess')}><select value={form.force_access} onChange={(event) => setForm((old) => ({ ...old, force_access: event.target.value }))} className="h-9 w-full rounded-md border bg-background px-3 text-sm"><option value="inherit">{t('admin.inherit')}</option><option value="allow">{t('admin.allow')}</option><option value="deny">{t('admin.deny')}</option></select></Field>
            <Field label={t('admin.expiry')}><Input type="datetime-local" value={form.expires_at} onChange={(event) => setForm((old) => ({ ...old, expires_at: event.target.value }))} /></Field>
            <Field label={t('admin.reason')}><Input value={form.reason} onChange={(event) => setForm((old) => ({ ...old, reason: event.target.value }))} placeholder={t('admin.reasonPlaceholder')} /></Field>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium">{t('admin.features')}</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {FEATURE_FIELDS.map(([field, labelKey]) => (
                <label key={field} className="flex items-center justify-between gap-3 rounded-md border p-3 text-sm">
                  <span>{t(labelKey)}</span>
                  <select
                    value={form.features[field] === true ? 'allow' : form.features[field] === false ? 'deny' : 'inherit'}
                    onChange={(event) => setForm((old) => {
                      const features = { ...old.features };
                      if (event.target.value === 'inherit') delete features[field];
                      else features[field] = event.target.value === 'allow';
                      return { ...old, features };
                    })}
                    className="h-8 rounded-md border bg-background px-2 text-xs"
                  >
                    <option value="inherit">{t('admin.inherit')}</option>
                    <option value="allow">{t('admin.allow')}</option>
                    <option value="deny">{t('admin.deny')}</option>
                  </select>
                </label>
              ))}
            </div>
          </div>
          <div><p className="mb-2 text-sm font-medium">{t('admin.limits')}</p><div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{LIMIT_FIELDS.map(([field, labelKey]) => <Field key={field} label={t(labelKey)}><Input type="number" min="-1" value={form.limits[field] ?? ''} onChange={(event) => setForm((old) => ({ ...old, limits: { ...old.limits, [field]: event.target.value } }))} /></Field>)}</div></div>
          {selected?.entitlements?.override?.active && <div className="rounded-md border border-destructive/30 p-3"><Field label={t('admin.clearReason')}><Input value={clearReason} onChange={(event) => setClearReason(event.target.value)} /></Field><Button className="mt-3" variant="destructive" onClick={clearOverride} disabled={saving || clearReason.trim().length < 3}>{t('common.clear')}</Button></div>}
          <DialogFooter><Button variant="outline" onClick={() => setSelected(null)}>{t('common.cancel')}</Button><Button onClick={saveOverride} disabled={saving || form.reason.trim().length < 3}>{t('common.save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) { return <label className="block"><span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>{children}</label>; }
function toLocalInput(value) { if (!value) return ''; const date = new Date(value); const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000); return local.toISOString().slice(0, 16); }
